<script setup lang="ts">
import buddhaImage from "@/assets/buddha.webp";
import { useI18n } from "vue-i18n";
import BaseModal from "@/views/Auth/components/BaseModal.vue";
import TextInput from "@/components/inputs/TextInput.vue";
import { computed, onMounted, ref } from "vue";
import ConfirmButton from "@/views/Auth/components/ConfirmButton.vue";
import { useRoute } from "vue-router";
import router from "@/router";
import { ROUTE_NAMES } from "@/constants/routeNames";
import { fetchChangePassword } from "@/api/user";

onMounted(() => {
  const queryToken = route.query.token as string | null;
  const queryEmail = route.query.email as string | null;
  if (!queryToken || !queryEmail) {
    router.replace({ name: ROUTE_NAMES.HOME });
  } else {
    token.value = queryToken;
    email.value = queryEmail;
  }
});

const route = useRoute();
const { t } = useI18n();

const email = ref<string>("");
const token = ref<string>("");
const newPassword = ref<string>("");
const newPasswordConfirm = ref<string>("");
const waitApiCall = ref(false);

const isNewPasswordValid = computed(() => {
  const passwordsInNotEmpty =
    newPassword.value.length > 0 && newPasswordConfirm.value.length > 0;
  const passwordsAreTheSame = newPassword.value === newPasswordConfirm.value;

  return passwordsAreTheSame && passwordsInNotEmpty;
});

async function handleResetPassword() {
  try {
    waitApiCall.value = true;
    await fetchChangePassword({
      new_password: newPassword.value,
      new_password_confirmation: newPasswordConfirm.value,
      email: email.value,
      token: token.value,
    });
    await router.replace({ name: ROUTE_NAMES.USER });
  } catch (error) {
    console.log(error);
  } finally {
    waitApiCall.value = false;
  }
}
</script>

<template>
  <div class="w-screen h-screen flex items-center justify-center">
    <BaseModal
      :show="true"
      :left-image-asset="buddhaImage"
      :show-close-icon="false"
    >
      <!-- TITLE -->
      <div class="flex gap-2">
        <p class="text-2xl font-semibold text-black mb-10 select-none">
          {{ t("auth.reset-password-title") }}
        </p>
      </div>
      <TextInput
        :label="t('auth.new-password')"
        v-model="newPassword"
        type="password"
        class="mb-4"
      />
      <TextInput
        :label="t('auth.confirm-new-password')"
        v-model="newPasswordConfirm"
        type="password"
        class="mb-10"
      />
      <ConfirmButton
        :disabled="!isNewPasswordValid || waitApiCall"
        :loading="waitApiCall"
        :button-text="t('auth.reset-password-button')"
        @click="handleResetPassword"
      />
    </BaseModal>
  </div>
</template>
